import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Divider,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SendIcon from '@mui/icons-material/Send';
import designCommentService from '../../services/designCommentService';

// ── Reply bubble ────────────────────────────────────────────────────────────

const ReplyBubble = ({ reply }) => (
  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        background: '#f0f0f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 10,
        fontWeight: 700,
        color: '#888',
        flexShrink: 0,
      }}
    >
      {(reply.author || 'U')[0].toUpperCase()}
    </Box>
    <Box sx={{ flex: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 0.25 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 11, color: '#333' }}>
          {reply.author || 'User'}
        </Typography>
        <Typography variant="caption" sx={{ fontSize: 10, color: '#bbb' }}>
          {reply.createdAt
            ? new Date(reply.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
            : ''}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ fontSize: 12, color: '#555', lineHeight: 1.5 }}>
        {reply.text}
      </Typography>
    </Box>
  </Box>
);

// ── CommentDialog ────────────────────────────────────────────────────────────

/**
 * CommentDialog — popover for viewing/creating a comment thread.
 *
 * Props:
 *   comment        existing comment object (null when creating new)
 *   commentNumber  1-based index for display
 *   onClose()
 *   onSave(text)   called when user submits a new comment
 *   onDelete(id)
 *   onUpdate(id, data)
 *   onReply(id, text)
 *   onSendToClaude(id) — called when Claude revision stream starts, receives update events
 *   onDesignUpdated(files) — called after Claude finishes generating revised design
 */
const CommentDialog = ({
  comment,
  commentNumber,
  onClose,
  onSave,
  onDelete,
  onUpdate,
  onReply,
  onDesignUpdated,
}) => {
  const [newText, setNewText] = useState('');
  const [replyText, setReplyText] = useState('');
  const [saving, setSaving] = useState(false);
  const [replying, setReplying] = useState(false);
  const [sendingToClaude, setSendingToClaude] = useState(false);
  const [claudeProgress, setClaudeProgress] = useState('');
  const streamRef = useRef(null);

  const isNew = !comment;

  // ── Create new ──────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!newText.trim()) return;
    setSaving(true);
    try {
      await onSave(newText.trim());
      setNewText('');
    } finally {
      setSaving(false);
    }
  };

  // ── Reply ───────────────────────────────────────────────────────────────────

  const handleReply = async () => {
    if (!replyText.trim() || !comment) return;
    setReplying(true);
    try {
      await onReply(comment.id, replyText.trim());
      setReplyText('');
    } finally {
      setReplying(false);
    }
  };

  // ── Send to Claude ──────────────────────────────────────────────────────────

  const handleSendToClaude = async () => {
    if (!comment || sendingToClaude) return;
    setSendingToClaude(true);
    setClaudeProgress('');

    const stream = designCommentService.sendToClaude(comment.id);
    streamRef.current = stream;

    try {
      for await (const event of stream.events()) {
        if (event.error) {
          setClaudeProgress(`Error: ${event.content}`);
          break;
        }
        if (event.done) {
          setClaudeProgress('');
          onDesignUpdated?.(event.files || []);
          break;
        }
        if (event.content) {
          setClaudeProgress((p) => (p + event.content).slice(-120));
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setClaudeProgress(`Error: ${err.message}`);
      }
    } finally {
      setSendingToClaude(false);
      streamRef.current = null;
    }
  };

  // ── Resolve toggle ──────────────────────────────────────────────────────────

  const handleResolveToggle = () => {
    if (!comment) return;
    onUpdate(comment.id, { resolved: !comment.resolved });
  };

  return (
    <Paper
      elevation={4}
      sx={{
        width: 300,
        borderRadius: 2,
        overflow: 'hidden',
        background: '#fff',
        border: '1px solid #eee',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          py: 1,
          borderBottom: '1px solid #f0f0f0',
          background: '#fafafa',
          gap: 0.5,
        }}
      >
        {!isNew && (
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: comment.resolved ? '#16a34a' : '#e85d26',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 10,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {commentNumber}
          </Box>
        )}
        <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600, color: '#555', flex: 1 }}>
          {isNew ? 'New comment' : comment.author || 'Comment'}
        </Typography>
        {!isNew && (
          <>
            <Tooltip title={comment.resolved ? 'Reopen' : 'Resolve'}>
              <Checkbox
                checked={!!comment.resolved}
                onChange={handleResolveToggle}
                size="small"
                sx={{ p: 0.25 }}
              />
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => onDelete(comment.id)}
                sx={{ color: '#ccc', '&:hover': { color: '#e53e3e' } }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </>
        )}
        <IconButton size="small" onClick={onClose} sx={{ color: '#aaa' }}>
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ p: 1.5, maxHeight: 320, overflowY: 'auto' }}>
        {isNew ? (
          <Box>
            <TextField
              fullWidth
              multiline
              minRows={3}
              placeholder="Leave a comment…"
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSave();
              }}
              sx={{ mb: 1, '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: 1.5 } }}
            />
            <Button
              fullWidth
              variant="contained"
              onClick={handleSave}
              disabled={!newText.trim() || saving}
              startIcon={saving ? <CircularProgress size={12} color="inherit" /> : <SendIcon />}
              sx={{
                background: '#e85d26',
                textTransform: 'none',
                fontSize: 12,
                fontWeight: 600,
                borderRadius: 1.5,
                '&:hover': { background: '#cf4f1e' },
              }}
            >
              Comment
            </Button>
          </Box>
        ) : (
          <>
            {/* Original comment */}
            <Typography variant="body2" sx={{ fontSize: 13, color: '#333', mb: 1, lineHeight: 1.6 }}>
              {comment.text}
            </Typography>

            {/* Replies */}
            {(comment.replies || []).length > 0 && (
              <>
                <Divider sx={{ my: 1 }} />
                {comment.replies.map((r, i) => (
                  <ReplyBubble key={i} reply={r} />
                ))}
              </>
            )}

            {/* Claude progress */}
            {sendingToClaude && claudeProgress && (
              <Box sx={{ mt: 1, p: 1, background: '#f8f4ff', borderRadius: 1 }}>
                <Typography variant="caption" sx={{ fontSize: 10, color: '#8b5cf6', fontFamily: 'monospace', display: 'block' }}>
                  {claudeProgress}…
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Footer — reply + send to Claude */}
      {!isNew && (
        <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5, borderTop: '1px solid #f0f0f0' }}>
          <Box sx={{ display: 'flex', gap: 0.75, mb: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Reply…"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleReply(); }}
              sx={{ '& .MuiOutlinedInput-root': { fontSize: 12, borderRadius: 1.5 } }}
            />
            <IconButton
              size="small"
              onClick={handleReply}
              disabled={!replyText.trim() || replying}
              sx={{ background: '#f5f5f5', '&:hover': { background: '#eee' } }}
            >
              {replying
                ? <CircularProgress size={12} />
                : <SendIcon sx={{ fontSize: 14 }} />}
            </IconButton>
          </Box>

          <Button
            fullWidth
            variant="outlined"
            size="small"
            onClick={handleSendToClaude}
            disabled={sendingToClaude}
            startIcon={sendingToClaude
              ? <CircularProgress size={12} color="inherit" />
              : <AutoAwesomeIcon sx={{ fontSize: 14 }} />}
            sx={{
              fontSize: 11,
              textTransform: 'none',
              borderColor: '#8b5cf6',
              color: '#8b5cf6',
              borderRadius: 1.5,
              '&:hover': { borderColor: '#6d28d9', color: '#6d28d9', background: '#f5f3ff' },
            }}
          >
            {sendingToClaude ? 'Revising design…' : 'Send to Claude'}
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default CommentDialog;
